// GPX → GeoJSON for the importTrack mutation. No parsing library: GPX is
// simple XML, and DOMParser covers the trk/trkseg/trkpt subset we accept.
// The server derives all track stats from the GeoJSON; the client-side
// distance here is only for the pre-submit summary.

export type ParsedGpx = {
  /** LineString (one segment) or MultiLineString GeoJSON string, [lon, lat, ele] coords. */
  geojson: string;
  /** ISO timestamps aligned 1:1 with points, or null when any point lacks <time>. */
  pointTimes: string[] | null;
  /** <trk><name> or <metadata><name>, when present. */
  name: string | null;
  pointCount: number;
  /** Haversine estimate for the pre-submit summary. */
  distanceM: number;
};

// querySelector rather than getElementsByTagName throughout: unprefixed CSS
// type selectors match by local name in any namespace, so prefixed GPX
// documents parse the same as default-namespaced ones.
export function parseGpx(text: string): ParsedGpx {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("This file is not valid GPX (XML parsing failed).");
  }

  type Pt = { coord: [number, number, number]; time: string | null };
  const segments: Pt[][] = [];
  for (const seg of doc.querySelectorAll("trkseg")) {
    const pts: Pt[] = [];
    for (const pt of seg.querySelectorAll("trkpt")) {
      const lat = Number(pt.getAttribute("lat"));
      const lon = Number(pt.getAttribute("lon"));
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        throw new Error("This GPX file has a track point without a valid lat/lon.");
      }
      const eleText = pt.querySelector("ele")?.textContent?.trim();
      const ele = eleText ? Number(eleText) : Number.NaN;
      pts.push({
        coord: [lon, lat, Number.isFinite(ele) ? ele : 0],
        time: pt.querySelector("time")?.textContent?.trim() || null,
      });
    }
    // A LineString part needs at least 2 positions; drop degenerate segments.
    if (pts.length >= 2) segments.push(pts);
  }

  const pointCount = segments.reduce((n, s) => n + s.length, 0);
  if (pointCount < 2) {
    throw new Error("This GPX file has fewer than 2 track points — nothing to import.");
  }

  let distanceM = 0;
  for (const seg of segments) {
    for (let i = 1; i < seg.length; i++) distanceM += haversineM(seg[i - 1]!.coord, seg[i]!.coord);
  }

  // pointTimes must align 1:1 with the points; if any point lacks a time,
  // send none at all rather than a misaligned array.
  const allPts = segments.flat();
  const pointTimes = allPts.every((p) => p.time != null) ? allPts.map((p) => p.time!) : null;

  const parts = segments.map((seg) => seg.map((p) => p.coord));
  const geojson = JSON.stringify(
    parts.length === 1
      ? { type: "LineString", coordinates: parts[0] }
      : { type: "MultiLineString", coordinates: parts },
  );

  const name =
    doc.querySelector("trk > name")?.textContent?.trim() ||
    doc.querySelector("metadata > name")?.textContent?.trim() ||
    null;

  return { geojson, pointTimes, name, pointCount, distanceM };
}

function haversineM(a: [number, number, number], b: [number, number, number]): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLon / 2) ** 2;
  // min-clamp: float error can push s past 1, and asin(>1) is NaN.
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
